import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Users, Phone, MapPin, Edit2, Trash2, Flag, Crown, X, UserPlus, Filter, SlidersHorizontal, MoreVertical, Activity, ExternalLink } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, Modal, ModalHeader, ModalBody, ModalFooter, EmptyState, SearchInput, MultiSelect, SlidePanel, Input, Textarea, PaginationBar, DeleteConfirmModal } from '../../../components/ui'
import { maskCPF, unmaskCPF, maskPhone, unmaskPhone, maskCEP, unmaskCEP, formatPhone } from '../../../utils/masks'
import { useAuth } from '../../../contexts/AuthContext'
import { inputClass } from '../../../styles/forms'
import CreateVoterModal from '../../../components/CreateVoterModal'
import LeaderSelect from '../../../components/LeaderSelect'
import RegisterActivityModal from '../../../components/RegisterActivityModal'

interface Voter {
  id: number
  name: string
  nickname: string | null
  cpf: string | null
  phone: string
  email: string | null
  status_code: string
  status_label: string
  channel_code: string | null
  channel_label: string | null
  city_name: string | null
  state_code: string | null
  neighborhood_name: string | null
  leader_name: string | null
}

interface Channel { id: number; code: string; label: string }
interface ActiveCampaign { id: number; name: string; is_active_campaign: boolean }

interface VotersPageProps {
  fixedView?: 'contacts' | 'voters'
}

export default function VotersPage({ fixedView }: VotersPageProps) {
  const navigate = useNavigate()
  const { accessProfile, leaderId, leaderPermissions } = useAuth()
  const isLeader = accessProfile?.code === 'ORG_LEADER'
  const canViewAll = leaderPermissions?.can_view_all_voters ?? false
  const [voters, setVoters] = useState<Voter[]>([])
  const [channels, setChannels] = useState<Channel[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10, last_page: 1 })
  const [viewMode, setViewMode] = useState<'all' | 'voters' | 'contacts' | 'campaign'>(fixedView || 'all')
  const [activeCampaign, setActiveCampaign] = useState<ActiveCampaign | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editPanelOpen, setEditPanelOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedVoter, setSelectedVoter] = useState<Voter | null>(null)
  const [actionsVoter, setActionsVoter] = useState<Voter | null>(null)
  const [actionsOpen, setActionsOpen] = useState(false)
  const [activityModalOpen, setActivityModalOpen] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [states, setStates] = useState<{ id: number; code: string; name: string }[]>([])
  const [filterCities, setFilterCities] = useState<{ id: number; name: string }[]>([])
  const [leaders, setLeaders] = useState<{ id: number; name: string; nickname: string | null }[]>([])
  const [filters, setFilters] = useState({
    channels: [] as string[], leader_ids: [] as string[], geo_state_id: '', geo_city_ids: [] as string[],
    electoral_zone: '', gender: '', campaign: '',
    birth_date_from: '', birth_date_to: '',
  })
  const [tempFilters, setTempFilters] = useState(filters)
  const [leadersLoaded, setLeadersLoaded] = useState(false)
  const [familyMode, setFamilyMode] = useState(false)
  const [familyName, setFamilyName] = useState('')
  const [familyMembers, setFamilyMembers] = useState<{ name: string; nickname: string; phone: string; cpf: string; email: string; voter_registration: string; electoral_zone: string; electoral_section: string }[]>([])

  const [form, setForm] = useState({
    name: '', nickname: '', cpf: '', phone: '', email: '',
    birth_date: '', gender: '',
    voter_registration: '', electoral_zone: '', electoral_section: '',
    zip_code: '', street: '', number: '', neighborhood: '', complement: '',
    geo_city_id: '', geo_neighborhood_id: '', city_display: '',
    capture_channel_id: '', leader_id: '', notes: '',
    add_to_campaign: false,
  })

  const activeFilterCount = [
    filters.channels.length > 0, filters.leader_ids.length > 0,
    filters.geo_state_id, filters.geo_city_ids.length > 0, filters.electoral_zone, filters.gender, filters.campaign,
    filters.birth_date_from, filters.birth_date_to,
  ].filter(Boolean).length

  const fetchVoters = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { search, page, per_page: perPage }
      // Aplicar filtro do fixedView sempre
      if (fixedView === 'voters' || viewMode === 'voters') params.is_voter = 1
      if (fixedView === 'contacts' || viewMode === 'contacts') params.is_contact = 1
      if (viewMode === 'campaign' && activeCampaign) params.campaign_id = activeCampaign.id
      if (filters.channels.length > 0) params.channel = filters.channels.join(',')
      if (isLeader && leaderId && !canViewAll) params.leader_id = String(leaderId)
      else if (filters.leader_ids.length > 0) params.leader_id = filters.leader_ids.join(',')
      if (filters.geo_city_ids.length > 0) params.geo_city_id = filters.geo_city_ids.join(',')
      if (filters.electoral_zone) params.electoral_zone = filters.electoral_zone
      if (filters.gender) params.gender = filters.gender
      if (filters.birth_date_from) params.birth_date_from = filters.birth_date_from
      if (filters.birth_date_to) params.birth_date_to = filters.birth_date_to
      if (filters.campaign && activeCampaign) params.campaign_id = activeCampaign.id
      const res = await axios.get(buildApiUrl('/v1/voters'), { params })
      setVoters(res.data.data)
      if (res.data.meta) setMeta(res.data.meta)
    } catch { /* */ } finally { setLoading(false) }
  }, [search, page, perPage, viewMode, activeCampaign, filters])

  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    Promise.all([
      axios.get(buildApiUrl('/v1/voters/channels')).then(r => setChannels(r.data.data)).catch(() => {}),
      axios.get(buildApiUrl('/v1/geo/states')).then(r => setStates(r.data.data)).catch(() => {}),
      axios.get(buildApiUrl('/v1/campaigns')).then(r => {
        const active = r.data.data?.find((c: any) => c.is_active_campaign)
        setActiveCampaign(active || null)
      }).catch(() => {}),
    ]).finally(() => setInitialized(true))
  }, [])

  useEffect(() => { if (initialized) fetchVoters() }, [initialized, fetchVoters])

  useEffect(() => {
    if (tempFilters.geo_state_id) {
      axios.get(buildApiUrl(`/v1/geo/states/${tempFilters.geo_state_id}/cities`))
        .then(r => setFilterCities(r.data.data)).catch(() => setFilterCities([]))
    } else {
      setFilterCities([])
    }
    setTempFilters(f => ({ ...f, geo_city_ids: [] }))
  }, [tempFilters.geo_state_id])

  function loadLeaders() {
    if (leadersLoaded) return
    axios.get(buildApiUrl('/v1/leaders'), { params: { per_page: 100 } })
      .then(r => { setLeaders(r.data.data.map((l: any) => ({ id: l.id, name: l.name, nickname: l.nickname }))); setLeadersLoaded(true) })
      .catch(() => {})
  }

  function resetForm() {
    setForm({
      name: '', nickname: '', cpf: '', phone: '', email: '',
      birth_date: '', gender: '',
      voter_registration: '', electoral_zone: '', electoral_section: '',
      zip_code: '', street: '', number: '', neighborhood: '', complement: '',
      geo_city_id: '', geo_neighborhood_id: '', city_display: '',
      capture_channel_id: '', leader_id: '', notes: '',
      add_to_campaign: !!activeCampaign,
    })
  }

  function openCreate() {
    setCreateModalOpen(true)
  }

  function addFamilyMember() {
    setFamilyMembers([...familyMembers, { name: '', nickname: '', phone: '', cpf: '', email: '', voter_registration: '', electoral_zone: '', electoral_section: '' }])
  }

  function updateFamilyMember(index: number, field: string, value: string) {
    setFamilyMembers(familyMembers.map((m, i) => i === index ? { ...m, [field]: value } : m))
  }

  function removeFamilyMember(index: number) {
    setFamilyMembers(familyMembers.filter((_, i) => i !== index))
  }

  async function openEdit(voter: Voter) {
    try {
      const res = await axios.get(buildApiUrl(`/v1/voters/${voter.id}`))
      const v = res.data.data.voter || res.data.data
      setSelectedVoter(voter)
      setForm({
        name: v.name || '', nickname: v.nickname || '',
        cpf: v.cpf ? maskCPF(v.cpf) : '', phone: v.phone ? maskPhone(v.phone) : '',
        email: v.email || '', birth_date: v.birth_date || '', gender: v.gender || '',
        voter_registration: v.voter_registration || '',
        electoral_zone: v.electoral_zone || '', electoral_section: v.electoral_section || '',
        zip_code: v.zip_code ? maskCEP(v.zip_code) : '',
        street: v.street || '', number: v.number || '',
        neighborhood: v.neighborhood || '', complement: v.complement || '',
        geo_city_id: v.geo_city_id?.toString() || '',
        geo_neighborhood_id: v.geo_neighborhood_id?.toString() || '',
        city_display: v.city_name && v.state_code ? `${v.city_name}/${v.state_code}` : '',
        capture_channel_id: v.capture_channel_id?.toString() || '',
        leader_id: v.leader_id?.toString() || '',
        notes: v.notes || '', add_to_campaign: false,
      })
      setEditPanelOpen(true)
    } catch { toast('error', 'Erro ao carregar eleitor') }
  }

  function openDelete(voter: Voter) {
    setSelectedVoter(voter)
    setDeleteModalOpen(true)
  }

  function openActions(voter: Voter) {
    setActionsVoter(voter)
    setActionsOpen(true)
  }

  function openActivity(voter: Voter) {
    setActionsVoter(voter)
    setActionsOpen(false)
    setActivityModalOpen(true)
  }

  async function handleCepLookup() {
    const cep = unmaskCEP(form.zip_code)
    if (cep.length < 8) return
    setCepLoading(true)
    try {
      const res = await axios.get(buildApiUrl(`/v1/geo/cep/${cep}`))
      const d = res.data.data
      setForm(f => ({
        ...f,
        street: d.street || f.street, neighborhood: d.neighborhood || f.neighborhood,
        geo_city_id: d.geo_city_id?.toString() || f.geo_city_id,
        geo_neighborhood_id: d.geo_neighborhood_id?.toString() || f.geo_neighborhood_id,
        city_display: d.city_name && d.state_code ? `${d.city_name}/${d.state_code}` : f.city_display,
      }))
    } catch { /* */ } finally { setCepLoading(false) }
  }

  async function handleSave() {
    if (saving) return
    setSaving(true)
    const payload: any = {
      name: form.name, nickname: form.nickname || null,
      cpf: unmaskCPF(form.cpf) || null, phone: unmaskPhone(form.phone),
      email: form.email || null, birth_date: form.birth_date || null, gender: form.gender || null,
      voter_registration: form.voter_registration || null,
      electoral_zone: form.electoral_zone || null, electoral_section: form.electoral_section || null,
      zip_code: unmaskCEP(form.zip_code) || null, street: form.street || null,
      number: form.number || null, neighborhood: form.neighborhood || null, complement: form.complement || null,
      geo_city_id: form.geo_city_id ? parseInt(form.geo_city_id) : null,
      geo_neighborhood_id: form.geo_neighborhood_id ? parseInt(form.geo_neighborhood_id) : null,
      capture_channel_id: form.capture_channel_id ? parseInt(form.capture_channel_id) : null,
      leader_id: isLeader && leaderId ? leaderId : (form.leader_id ? parseInt(form.leader_id) : null),
      notes: form.notes || null, add_to_campaign: form.add_to_campaign,
      is_contact: fixedView === 'contacts',
      is_voter: fixedView !== 'contacts',
    }

    try {
      if (selectedVoter) {
        await axios.put(buildApiUrl(`/v1/voters/${selectedVoter.id}`), payload)
        toast('success', 'Eleitor atualizado')
      } else if (familyMode) {
        // Cadastro familiar
        const allMembers = [
          { name: form.name, nickname: form.nickname || null, phone: unmaskPhone(form.phone), cpf: unmaskCPF(form.cpf) || null, email: form.email || null },
          ...familyMembers.filter(m => m.name && m.phone).map(m => ({
            name: m.name, nickname: m.nickname || null, phone: unmaskPhone(m.phone), cpf: unmaskCPF(m.cpf) || null, email: m.email || null,
            voter_registration: m.voter_registration || null, electoral_zone: m.electoral_zone || null, electoral_section: m.electoral_section || null,
          })),
        ]
        const familyPayload = {
          family_name: form.nickname ? `Família ${form.nickname}` : `Família ${form.name.split(' ').pop()}`,
          zip_code: unmaskCEP(form.zip_code) || null,
          street: form.street || null, number: form.number || null,
          neighborhood: form.neighborhood || null, complement: form.complement || null,
          geo_city_id: form.geo_city_id ? parseInt(form.geo_city_id) : null,
          geo_neighborhood_id: form.geo_neighborhood_id ? parseInt(form.geo_neighborhood_id) : null,
          capture_channel_id: form.capture_channel_id ? parseInt(form.capture_channel_id) : null,
          add_to_campaign: form.add_to_campaign,
          members: allMembers,
        }
        const res = await axios.post(buildApiUrl('/v1/voters/family'), familyPayload)
        const data = res.data.data
        if (data.errors?.length > 0) {
          toast('warning', `${data.created_count} cadastrado(s), ${data.errors.length} erro(s)`, data.errors[0])
        } else {
          toast('success', `${data.created_count} membro(s) cadastrado(s)`)
        }
      } else {
        await axios.post(buildApiUrl('/v1/voters'), payload)
        toast('success', fixedView === 'contacts' ? 'Contato cadastrado' : 'Eleitor cadastrado')
      }
      setModalOpen(false)
      fetchVoters()
    } catch (err: any) {
      toast('error', err.response?.data?.error || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  async function handleDelete() {
    if (!selectedVoter) return
    try {
      await axios.delete(buildApiUrl(`/v1/voters/${selectedVoter.id}`))
      toast('success', 'Eleitor desativado')
      setDeleteModalOpen(false)
      fetchVoters()
    } catch { toast('error', 'Erro ao desativar') }
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{viewMode === 'contacts' ? 'Contatos' : viewMode === 'voters' ? 'Eleitores' : 'Eleitores e Contatos'}</h1>
          <p className="text-gray-500 mt-1">{viewMode === 'contacts' ? 'Contatos convertidos via formulários e integrações' : viewMode === 'voters' ? 'Eleitores cadastrados ativamente' : 'Base completa de contatos e eleitores'}</p>
        </div>
        <div className="flex items-center gap-2">
          {activeCampaign && (
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button onClick={() => { setViewMode(fixedView || 'all'); setFilters(f => ({ ...f, campaign: '' })); setPage(1) }} className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors', viewMode !== 'campaign' ? 'bg-brand-50 text-brand-600 shadow-xs' : 'text-gray-500')}>
                Todos
              </button>
              <button onClick={() => { setViewMode('campaign'); setFilters(f => ({ ...f, campaign: 'active' })); setPage(1) }} className={clsx('px-3 py-1.5 rounded-md text-sm font-medium transition-colors', viewMode === 'campaign' ? 'bg-brand-50 text-brand-600 shadow-xs' : 'text-gray-500')}>
                Campanha
              </button>
            </div>
          )}
          <Button icon={<Plus className="w-4 h-4" />} onClick={openCreate}>
            {fixedView === 'contacts' ? 'Novo Contato' : 'Novo Eleitor'}
          </Button>
        </div>
      </div>

      {/* Busca + Filtros */}
      <div className="flex gap-2">
        <SearchInput
          placeholder="Buscar por nome, apelido, telefone, email, CPF..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
          className="flex-1"
        />
        <button onClick={() => { setTempFilters({ ...filters, campaign: viewMode === 'campaign' ? 'active' : '' }); setFilterModalOpen(true) }} className={clsx(
          'flex items-center gap-2 h-11 px-4 rounded-lg text-sm font-medium transition-colors border',
          activeFilterCount > 0
            ? 'bg-brand-50 border-brand-200 text-brand-600'
            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
        )}>
          <SlidersHorizontal className="w-4 h-4" />
          Filtros
          {activeFilterCount > 0 && (
            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-500 text-white text-[10px] font-bold">{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* Tabela */}
      {loading ? (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cidade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Canal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[1,2,3,4,5].map(i => (
                  <tr key={i}>
                    <td className="px-4 py-3.5"><div className="h-4 w-40 bg-gray-100 rounded animate-pulse" /></td>
                    <td className="px-4 py-3.5"><div className="h-4 w-32 bg-gray-100 rounded animate-pulse" /></td>
                    <td className="px-4 py-3.5 hidden sm:table-cell"><div className="h-4 w-28 bg-gray-100 rounded animate-pulse" /></td>
                    <td className="px-4 py-3.5 hidden sm:table-cell"><div className="h-4 w-20 bg-gray-100 rounded animate-pulse" /></td>
                    <td className="px-4 py-3.5 hidden sm:table-cell"><div className="h-5 w-16 bg-gray-100 rounded-full animate-pulse" /></td>
                    <td className="px-4 py-3.5 hidden sm:table-cell"><div className="h-4 w-16 bg-gray-100 rounded animate-pulse ml-auto" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : voters.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users className="w-7 h-7" />}
            title={viewMode === 'campaign' ? 'Nenhum registro na campanha' : fixedView === 'contacts' ? 'Nenhum contato' : 'Nenhum eleitor cadastrado'}
            description={viewMode === 'campaign' ? 'Adicione à campanha ativa.' : fixedView === 'contacts' ? 'Os contatos aparecem quando convertem via formulários e integrações.' : 'Cadastre eleitores para começar a construir sua base.'}
            action={<Button size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Cadastrar</Button>}
          />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Bairro / Cidade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Canal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {voters.map(voter => (
                  <tr key={voter.id} className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      if (window.innerWidth < 640) { openActions(voter) } else { navigate(`/${fixedView === 'contacts' ? 'contacts' : 'voters'}/${voter.id}`) }
                    }}>
                    <td className="px-4 py-3.5">
                      <p className="text-sm font-medium text-gray-900">{voter.name}</p>
                      {voter.nickname && <p className="text-xs text-gray-400">{voter.nickname}</p>}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-700">
                      {formatPhone(voter.phone)}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-700 hidden sm:table-cell">
                      {voter.neighborhood_name && <span>{voter.neighborhood_name}, </span>}
                      {voter.city_name ? `${voter.city_name}/${voter.state_code}` : '—'}
                    </td>
                    <td className="px-4 py-3.5 hidden sm:table-cell">
                      {voter.channel_label ? (
                        <span className="text-xs text-gray-500">{voter.channel_label}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3.5 hidden sm:table-cell">
                      <Badge variant={voter.status_code === 'ACTIVE' ? 'success' : 'gray'} dot size="sm">{voter.status_label}</Badge>
                    </td>
                    <td className="px-4 py-3.5 text-right hidden sm:table-cell">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={e => { e.stopPropagation(); openActivity(voter) }} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-brand-500 hover:bg-brand-50 transition-colors" title="Registrar atividade">
                          <Activity className="w-4 h-4" />
                        </button>
                        <button onClick={e => { e.stopPropagation(); openEdit(voter) }} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="Editar">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={e => { e.stopPropagation(); openDelete(voter) }} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-error-500 hover:bg-error-50 transition-colors" title="Excluir">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PaginationBar page={page} perPage={perPage} total={meta.total} lastPage={meta.last_page} onPageChange={setPage} onPerPageChange={n => { setPerPage(n); setPage(1) }} />
        </Card>
      )}

      {/* Painel filtros */}
      <SlidePanel open={filterModalOpen} onClose={() => setFilterModalOpen(false)} title="Filtros" size="md">
        <div className="p-6 space-y-4">
          {activeCampaign && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Campanha</label>
              <select value={tempFilters.campaign} onChange={e => setTempFilters({ ...tempFilters, campaign: e.target.value })} className={clsx(inputClass, 'appearance-none')}>
                <option value="">Todos os eleitores</option>
                <option value="active">Engajados na campanha atual</option>
              </select>
            </div>
          )}

          <MultiSelect
            label="Canal de captação"
            options={channels.map(c => ({ value: c.code, label: c.label }))}
            selected={tempFilters.channels}
            onChange={v => setTempFilters({ ...tempFilters, channels: v })}
            placeholder="Todos os canais"
          />

          {!isLeader && (
            <MultiSelect
              label="Liderança"
              options={leaders.map(l => ({ value: l.id.toString(), label: l.nickname ? `${l.name} (${l.nickname})` : l.name }))}
              selected={tempFilters.leader_ids}
              onChange={v => setTempFilters({ ...tempFilters, leader_ids: v })}
              placeholder="Todas as lideranças"
              lazy
              onOpen={loadLeaders}
            />
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Estado</label>
            <select value={tempFilters.geo_state_id} onChange={e => setTempFilters({ ...tempFilters, geo_state_id: e.target.value })} className={clsx(inputClass, 'appearance-none')}>
              <option value="">Todos</option>
              {states.map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
            </select>
          </div>

          <MultiSelect
            label="Cidade"
            options={filterCities.map(c => ({ value: String(c.id), label: c.name }))}
            selected={tempFilters.geo_city_ids}
            onChange={v => setTempFilters({ ...tempFilters, geo_city_ids: v })}
            placeholder={tempFilters.geo_state_id ? 'Todas as cidades' : 'Selecione o estado'}
          />

          <MultiSelect
            label="Gênero"
            options={[{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Feminino' }, { value: 'O', label: 'Outro' }]}
            selected={tempFilters.gender ? [tempFilters.gender] : []}
            onChange={v => setTempFilters({ ...tempFilters, gender: v[v.length - 1] || '' })}
            placeholder="Todos"
            searchable={false}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Zona eleitoral</label>
            <input value={tempFilters.electoral_zone} onChange={e => setTempFilters({ ...tempFilters, electoral_zone: e.target.value })} placeholder="Ex: 001" className={inputClass} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Data de nascimento</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">De</label>
                <input type="date" value={tempFilters.birth_date_from} onChange={e => setTempFilters({ ...tempFilters, birth_date_from: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Até</label>
                <input type="date" value={tempFilters.birth_date_to} onChange={e => setTempFilters({ ...tempFilters, birth_date_to: e.target.value })} className={inputClass} />
              </div>
            </div>
          </div>

          <div className="pt-4 flex gap-3">
            <Button onClick={() => { setFilters(tempFilters); setViewMode(tempFilters.campaign === 'active' ? 'campaign' : 'all'); setFilterModalOpen(false) }} className="flex-1">Aplicar filtros</Button>
            <Button variant="outline" onClick={() => { const empty = { channels: [] as string[], leader_ids: [] as string[], geo_state_id: '', geo_city_ids: [] as string[], electoral_zone: '', gender: '', campaign: '', birth_date_from: '', birth_date_to: '' }; setTempFilters(empty); setFilters(empty); setFilterModalOpen(false) }} className="flex-1">Limpar</Button>
          </div>
        </div>
      </SlidePanel>

      {/* Modal criar/editar */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="lg">
        <ModalHeader onClose={() => setModalOpen(false)}>{selectedVoter ? 'Editar Eleitor' : 'Novo Eleitor'}</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            {/* Dados pessoais */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome completo <span className="text-error-500">*</span></label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nome do eleitor" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Apelido</label>
                <input value={form.nickname} onChange={e => setForm({ ...form, nickname: e.target.value })} placeholder="Como é conhecido" className={inputClass} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">CPF</label>
                <input value={form.cpf} onChange={e => setForm({ ...form, cpf: maskCPF(e.target.value) })} placeholder="000.000.000-00" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Telefone <span className="text-error-500">*</span></label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: maskPhone(e.target.value) })} placeholder="(82) 99999-9999" className={inputClass} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@exemplo.com" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Data de nascimento</label>
                <input type="date" value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} className={inputClass} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Gênero</label>
                <select value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })} className={clsx(inputClass, 'appearance-none')}>
                  <option value="">Selecione</option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                  <option value="O">Outro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Canal de captação</label>
                <select
                  value={form.capture_channel_id}
                  onChange={e => setForm({ ...form, capture_channel_id: e.target.value })}
                  disabled={!selectedVoter && form.capture_channel_id === String(channels.find(c => c.code === 'MANUAL')?.id || '')}
                  className={clsx(inputClass, 'appearance-none', !selectedVoter && 'bg-gray-50 text-gray-600 cursor-not-allowed')}
                >
                  <option value="">Selecione</option>
                  {channels.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {/* Liderança */}
            <LeaderSelect
              label="Liderança vinculada"
              value={form.leader_id}
              onChange={id => setForm({ ...form, leader_id: id })}
              initialName={selectedVoter?.leader_name || ''}
              dropdownZIndex={100003}
            />

            {/* Dados eleitorais */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Dados eleitorais</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Título de eleitor</label>
                  <input value={form.voter_registration} onChange={e => setForm({ ...form, voter_registration: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Zona eleitoral</label>
                  <input value={form.electoral_zone} onChange={e => setForm({ ...form, electoral_zone: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Seção</label>
                  <input value={form.electoral_section} onChange={e => setForm({ ...form, electoral_section: e.target.value })} className={inputClass} />
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Endereço</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">CEP</label>
                  <div className="relative">
                    <input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: maskCEP(e.target.value) })} onBlur={handleCepLookup} placeholder="00000-000" className={clsx(inputClass, 'pr-10')} />
                    {cepLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="w-4 h-4 border-2 border-brand-200 border-t-brand-500 rounded-full animate-spin" /></div>}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Rua</label>
                  <input value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 mt-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Número</label>
                  <input value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Bairro</label>
                  <input value={form.neighborhood} onChange={e => setForm({ ...form, neighborhood: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Complemento</label>
                  <input value={form.complement} onChange={e => setForm({ ...form, complement: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Cidade/UF</label>
                  <input value={form.city_display} readOnly placeholder="Preenchido pelo CEP" className="h-11 w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-sm text-gray-600 cursor-not-allowed" />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Observações</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Anotações..." className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none resize-none" />
            </div>

            {/* Cadastro familiar */}
            {!selectedVoter && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3 cursor-pointer" onClick={() => { setFamilyMode(!familyMode); if (!familyMode && familyMembers.length === 0) addFamilyMember() }}>
                    <div className={clsx('w-10 h-6 rounded-full transition-colors relative', familyMode ? 'bg-brand-500' : 'bg-gray-300')}>
                      <span className={clsx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-xs transition-transform', familyMode ? 'left-[18px]' : 'left-0.5')} />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Cadastro familiar</span>
                      <p className="text-xs text-gray-400">Cadastrar mais pessoas no mesmo endereço</p>
                    </div>
                  </div>
                </div>

                {familyMode && (
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Outros membros da família</p>

                    {familyMembers.map((member, i) => (
                      <div key={i} className="bg-gray-50 rounded-xl p-4 relative">
                        <button type="button" onClick={() => removeFamilyMember(i)} className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:text-error-500 hover:bg-error-50">
                          <X className="w-3.5 h-3.5" />
                        </button>
                        <p className="text-xs font-medium text-gray-500 mb-2">Membro {i + 2}</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <input value={member.name} onChange={e => updateFamilyMember(i, 'name', e.target.value)} placeholder="Nome *" className={clsx(inputClass, 'h-10 text-sm')} />
                          </div>
                          <div>
                            <input value={member.phone} onChange={e => updateFamilyMember(i, 'phone', maskPhone(e.target.value))} placeholder="Telefone *" className={clsx(inputClass, 'h-10 text-sm')} />
                          </div>
                          <div>
                            <input value={member.cpf} onChange={e => updateFamilyMember(i, 'cpf', maskCPF(e.target.value))} placeholder="CPF" className={clsx(inputClass, 'h-10 text-sm')} />
                          </div>
                          <div>
                            <input value={member.nickname} onChange={e => updateFamilyMember(i, 'nickname', e.target.value)} placeholder="Apelido" className={clsx(inputClass, 'h-10 text-sm')} />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mt-2">
                          <div>
                            <input value={member.voter_registration} onChange={e => updateFamilyMember(i, 'voter_registration', e.target.value)} placeholder="Título de eleitor" className={clsx(inputClass, 'h-10 text-sm')} />
                          </div>
                          <div>
                            <input value={member.electoral_zone} onChange={e => updateFamilyMember(i, 'electoral_zone', e.target.value)} placeholder="Zona" className={clsx(inputClass, 'h-10 text-sm')} />
                          </div>
                          <div>
                            <input value={member.electoral_section} onChange={e => updateFamilyMember(i, 'electoral_section', e.target.value)} placeholder="Seção" className={clsx(inputClass, 'h-10 text-sm')} />
                          </div>
                        </div>
                      </div>
                    ))}

                    <button type="button" onClick={addFamilyMember} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-sm text-gray-500 hover:border-brand-300 hover:text-brand-500 transition-colors">
                      <UserPlus className="w-4 h-4" /> Adicionar mais um membro
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Campanha */}
            {activeCampaign && !selectedVoter && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 cursor-pointer" onClick={() => setForm({ ...form, add_to_campaign: !form.add_to_campaign })}>
                    <div className={clsx('w-10 h-6 rounded-full transition-colors relative', form.add_to_campaign ? 'bg-brand-500' : 'bg-gray-300')}>
                      <span className={clsx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-xs transition-transform', form.add_to_campaign ? 'left-[18px]' : 'left-0.5')} />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-700">Engajar na campanha ativa</span>
                      <p className="text-xs text-gray-400">{activeCampaign.name}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!form.name || !form.phone} loading={saving}>{selectedVoter ? 'Salvar' : 'Cadastrar'}</Button>
        </ModalFooter>
      </Modal>

      {/* SlidePanel editar */}
      <SlidePanel open={editPanelOpen} onClose={() => setEditPanelOpen(false)} title={`Editar ${fixedView === 'contacts' ? 'contato' : 'eleitor'}`} subtitle={selectedVoter?.name}>
        <div className="p-6 space-y-4">
          <Input label="Nome" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <Input label="Apelido" value={form.nickname} onChange={e => setForm({ ...form, nickname: e.target.value })} />
          <Input label="Telefone" value={form.phone} onChange={e => setForm({ ...form, phone: maskPhone(e.target.value) })} />
          <Input label="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          <Input label="CPF" value={form.cpf} onChange={e => setForm({ ...form, cpf: maskCPF(e.target.value) })} />
          <Input label="Data de nascimento" type="date" value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} />

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Endereço</p>
            <div className="space-y-4">
              <Input label="CEP" value={form.zip_code} onChange={e => setForm({ ...form, zip_code: maskCEP(e.target.value) })} onBlur={handleCepLookup} />
              <Input label="Rua" value={form.street} onChange={e => setForm({ ...form, street: e.target.value })} />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Número" value={form.number} onChange={e => setForm({ ...form, number: e.target.value })} />
                <Input label="Bairro" value={form.neighborhood} onChange={e => setForm({ ...form, neighborhood: e.target.value })} />
              </div>
              <Input label="Complemento" value={form.complement} onChange={e => setForm({ ...form, complement: e.target.value })} />
              <Input label="Cidade/UF" value={form.city_display} readOnly />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Dados eleitorais</p>
            <div className="grid grid-cols-3 gap-4">
              <Input label="Título" value={form.voter_registration} onChange={e => setForm({ ...form, voter_registration: e.target.value })} />
              <Input label="Zona" value={form.electoral_zone} onChange={e => setForm({ ...form, electoral_zone: e.target.value })} />
              <Input label="Seção" value={form.electoral_section} onChange={e => setForm({ ...form, electoral_section: e.target.value })} />
            </div>
          </div>

          <Textarea label="Observações" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} />

          <div className="pt-4 flex gap-3">
            <Button onClick={async () => { await handleSave(); setEditPanelOpen(false) }} disabled={!form.name || !form.phone} loading={saving} className="flex-1">Salvar</Button>
            <Button variant="outline" onClick={() => setEditPanelOpen(false)} className="flex-1">Cancelar</Button>
          </div>
        </div>
      </SlidePanel>

      {/* Modal excluir */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setDeleteModalOpen(false)}>Desativar {fixedView === 'contacts' ? 'contato' : 'eleitor'}</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600">Tem certeza que deseja desativar <strong>{selectedVoter?.name}</strong>? O registro não será excluído permanentemente.</p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDelete}>Desativar</Button>
        </ModalFooter>
      </Modal>

      <CreateVoterModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => { setCreateModalOpen(false); fetchVoters() }}
        type={isLeader || viewMode === 'voters' ? 'voter' : 'contact'}
      />

      {/* Bottom sheet ações (mobile) */}
      {actionsOpen && actionsVoter && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setActionsOpen(false)} />
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 animate-in slide-in-from-bottom pb-safe">
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mt-3" />
            <div className="p-4 border-b border-gray-100">
              <p className="font-semibold text-gray-900">{actionsVoter.name}</p>
              <p className="text-xs text-gray-500">{formatPhone(actionsVoter.phone)}</p>
            </div>
            <div className="p-2">
              <button onClick={() => { setActionsOpen(false); navigate(`/${fixedView === 'contacts' ? 'contacts' : 'voters'}/${actionsVoter.id}`) }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 text-left">
                <ExternalLink className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-700">Ver perfil</span>
              </button>
              <button onClick={() => openActivity(actionsVoter)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 text-left">
                <Activity className="w-5 h-5 text-brand-500" />
                <span className="text-sm text-gray-700">Registrar atividade</span>
              </button>
              <button onClick={() => { setActionsOpen(false); openEdit(actionsVoter) }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 text-left">
                <Edit2 className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-700">Editar</span>
              </button>
              <button onClick={() => { setActionsOpen(false); openDelete(actionsVoter) }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 text-left">
                <Trash2 className="w-5 h-5 text-error-400" />
                <span className="text-sm text-error-500">Excluir</span>
              </button>
            </div>
          </div>
        </>
      )}

      <RegisterActivityModal
        open={activityModalOpen}
        onClose={() => setActivityModalOpen(false)}
        voter={actionsVoter}
      />
    </div>
  )
}
